import React, { useContext, useEffect, useState } from "react";
import { AppContext } from "../../../../store/AppContext";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import ShippingDetailsModel from "../Orders/components/ShippingDetailsModel";
import CustomDialog from "../../../../components/CustomDialog/CustomDialog";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { auth, db } from "../../../../firebase";
import { apiFetch } from "../../../../utils/api";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckIcon from "@mui/icons-material/Check";
import { Close, HourglassEmpty } from "@mui/icons-material";
import { useMediaQuery, useTheme, Tabs, Tab } from "@mui/material";
import SwipeableViews from "react-swipeable-views";

const ViewOrder = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [activeStep, setActiveStep] = React.useState(0);
  const [open, setOpen] = useState(false);
  const [open2, setOpen2] = useState(false);
  const [ship, setShip] = useState(false);
  const [mobileTabValue, setMobileTabValue] = useState(0);
  const [shippingDetails, setShippingDetails] = React.useState({
    shippingPartner: "",
    trackingNumber: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setShippingDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const { orderTabInfo, setOrderTabInfo, setHeaderData, setLoader } =
    useContext(AppContext);
  const selectedOrder = orderTabInfo?.selectedOrder;

  const {
    order_id,
    id,
    timestamp,
    status,
    quantity_each,
    items_total,
    coupon_discount,
    coupon_code,
    delivery,
    customer_details,
    payment_mode,
    additional_info,
    trackingInfo,
  } = selectedOrder || {};

  const getSteps = (orderStatus, trackingInfo) => {
    const lowerStatus = orderStatus?.toLowerCase();
    const baseSteps = [
      {
        label: "Order Placed",
        description: "Your order has been received",
        icon: <ReceiptIcon />,
        active: lowerStatus === "pending",
        completed: true,
      },
      {
        label: "Order Processing",
        description:
          lowerStatus === "accepted" || lowerStatus === "shipped"
            ? `Accepted on ${new Date(
                selectedOrder.acceptedAt?.seconds * 1000
              ).toLocaleString()}`
            : lowerStatus === "rejected"
            ? "Order was rejected"
            : lowerStatus === "expired"
            ? "Order expired"
            : "Waiting for processing",
        icon: <HourglassEmpty />,
        active: lowerStatus === "pending",
        completed: ["accepted", "shipped", "rejected", "expired"].includes(
          lowerStatus
        ),
      },
      {
        label: "Order Fulfillment",
        description:
          lowerStatus === "shipped"
            ? trackingInfo?.trackingId &&
              `Shipped via ${trackingInfo?.courier} (Tracking #: ${trackingInfo?.trackingId})`
            : lowerStatus === "accepted"
            ? "Preparing for shipment"
            : "Not applicable",
        icon: <LocalShippingIcon />,
        active: lowerStatus === "accepted",
        completed: lowerStatus === "shipped",
      },
    ];

    return baseSteps;
  };

  useEffect(() => {
    const currentStatus = selectedOrder?.status?.toLowerCase();

    if (currentStatus === "pending" || currentStatus === "paid") {
      setHeaderData((prevState) => {
        return {
          ...prevState,
          rightActionText: "Accept order",
          rightActionSecondaryText: "Reject order",
          rightActionSecondary: () => setOpen2(true),
          rightAction: () => setOpen(true),
        };
      });
    } else if (currentStatus === "accepted") {
      setHeaderData((prevState) => {
        return {
          ...prevState,
          rightActionSecondaryText: "",
          rightActionText: "Ship order",
          rightAction: () => setShip(true),
        };
      });
    } else if (currentStatus === "shipped") {
      setHeaderData((prevState) => {
        return {
          ...prevState,
          rightActionText: "",
          leftText: "Order Details",
          rightAction: null,
        };
      });
    } else if (
      currentStatus === "rejected" ||
      currentStatus === "expired" ||
      currentStatus === "cancelled"
    ) {
      setHeaderData((prevState) => {
        return {
          ...prevState,
          rightActionText: "",
          leftText: "Order Details",
          rightAction: null,
        };
      });
    }
  }, [selectedOrder?.status]);

  if (!selectedOrder) {
    return <div>No order selected</div>;
  }

  const orderDate = new Date(timestamp.seconds * 1000).toLocaleString();

  const handleAcceptOrder = async () => {
    try {
      setLoader(true);
      const response = await apiFetch(`/orders/accept/${id}`, {
        method: "POST",
        body: JSON.stringify({
          acceptedBy: auth.currentUser?.email || "admin",
        }),
      });

      if (!response.ok) {
        setLoader(false);
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to accept order");
      }

      const result = await response.json();
      console.log("Order accepted successfully:", result);
      setLoader(false);
      await fetchOrderData(id);
    } catch (err) {
      setLoader(false);
      console.error("Error accepting order:", err);
    } finally {
      setLoader(false);
      setOpen(false);
      setOrderTabInfo((prevState) => ({
        ...prevState,
        trigger: false,
      }));
    }
  };

  const handleRejectOrder = async () => {
    try {
      setLoader(true);
      const response = await apiFetch(`/orders/reject/${id}`, {
        method: "POST",
        body: JSON.stringify({
          rejectedBy: auth.currentUser?.email || "admin",
          reason: "Manual rejection",
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to reject order");
      }

      const result = await response.json();
      console.log("Order rejected successfully:", result);
      setLoader(false);
      await fetchOrderData(id);
    } catch (err) {
      setLoader(false);
      console.error("Error rejecting order:", err);
    } finally {
      setLoader(false);
      setOpen2(false);
      setOrderTabInfo((prevState) => ({
        ...prevState,
        trigger: false,
      }));
    }
  };

  const updateOrderToShipped = async (orderId, courierName, trackingId) => {
    try {
      setLoader(true);
      const response = await apiFetch(`/orders/ship/${orderId}`, {
        method: "POST",
        body: JSON.stringify({
          courier: courierName,
          trackingId: trackingId,
          shippedBy: auth.currentUser?.email || "admin",
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to ship order");
      }

      const result = await response.json();
      console.log("Order shipped successfully:", result);
      await fetchOrderData(orderId);
      return true;
    } catch (error) {
      console.error("Error shipping order:", error);
      return false;
    } finally {
      setLoader(false);
      setShip(false);
    }
  };

  const fetchOrderData = async (orderId) => {
    try {
      const ordersRef = collection(db, "orders");
      const q = query(ordersRef, where("id", "==", orderId));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const orderData = querySnapshot.docs[0].data();
        setOrderTabInfo((prevState) => ({
          ...prevState,
          selectedOrder: orderData,
        }));
      } else {
        console.log("Order not found!");
      }
    } catch (error) {
      console.error("Error fetching order:", error);
    }
  };

  const handleDownloadReceipt = async () => {
    try {
      setLoader(true);
      const response = await apiFetch(`/orders/receipt/${id}/pdf`, {
        method: "GET",
      });

      if (!response.ok) {
        let message = "Failed to download receipt";
        try {
          const errBody = await response.json();
          message = errBody.error || message;
        } catch {
          /* response may be non-JSON */
        }
        throw new Error(message);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `order-receipt-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading receipt:", error);
      alert(error.message || "Failed to download receipt");
    } finally {
      setLoader(false);
    }
  };

  const handleDownloadInvoice = async () => {
    try {
      setLoader(true);
      const response = await apiFetch(`/orders/invoice/${id}/pdf`, {
        method: "GET",
      });

      if (!response.ok) {
        let message = "Failed to download invoice";
        try {
          const errBody = await response.json();
          message = errBody.error || message;
        } catch {
          /* response may be non-JSON */
        }
        throw new Error(message);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `order-invoice-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading invoice:", error);
      alert(error.message || "Failed to download invoice");
    } finally {
      setLoader(false);
    }
  };

  // Mobile Tab Content Components
  const OrderDetailsContent = () => (
    <div style={{ padding: isMobile ? "16px" : "25px" }}>
      {/* Order Header */}
      <div
        style={{
          display: "flex",
          width: "100%",
          justifyContent: "space-between",
          alignItems: "center",
          padding: isMobile ? "16px 0" : "20px 0",
          flexWrap: isMobile ? "wrap" : "nowrap",
          gap: isMobile ? "8px" : "0",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <h3
            style={{
              fontSize: isMobile ? "1rem" : "1.1rem",
              fontWeight: "600",
              margin: 0,
            }}
          >
            Order ID #{isMobile ? id?.substring(0, 10) + "..." : id}
          </h3>
          <h6
            style={{
              fontSize: isMobile ? "0.8rem" : "0.9rem",
              color: "gray",
              fontWeight: "400",
              margin: 0,
            }}
          >
            {orderDate}
          </h6>
        </div>
        <div>
          <h5
            style={{
              fontSize: isMobile ? "0.875rem" : "1rem",
              fontWeight: "400",
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              padding: isMobile ? "4px 8px" : "5px 10px",
              borderRadius: "12px",
              margin: 0,
              color:
                status?.toLowerCase() === "shipped"
                  ? "#2E7D32"
                  : status?.toLowerCase() === "accepted"
                  ? "#388E3C"
                  : status?.toLowerCase() === "pending"
                  ? "#FB8C00"
                  : status?.toLowerCase() === "expired"
                  ? "#757575"
                  : "#D32F2F",
              backgroundColor:
                status?.toLowerCase() === "shipped"
                  ? "#E8F5E9"
                  : status?.toLowerCase() === "accepted"
                  ? "#E8F5E9"
                  : status?.toLowerCase() === "pending"
                  ? "#FFF3E0"
                  : status?.toLowerCase() === "expired"
                  ? "#F5F5F5"
                  : "#FFEBEE",
              ...(!status && {
                color: "gray",
                backgroundColor: "transparent",
              }),
            }}
          >
            {status?.toUpperCase() ?? "UNKNOWN"}
          </h5>
        </div>
      </div>
      <div style={{ borderBottom: "1px solid #e0e0e0", width: "100%" }}></div>

      {/* Order Items */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          padding: isMobile ? "16px 0" : "20px 0",
        }}
      >
        <div
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            marginBottom: isMobile ? "16px" : "20px",
          }}
        >
          <h4
            style={{
              fontSize: isMobile ? "0.9rem" : "1rem",
              color: "gray",
              fontWeight: "500",
              margin: 0,
            }}
          >
            {quantity_each?.length || 0} ITEMS
          </h4>
          <h4
            style={{
              fontSize: isMobile ? "0.875rem" : "1rem",
              border: ".5px solid blue",
              borderRadius: "5px",
              fontWeight: "500",
              alignItems: "center",
              display: "flex",
              padding: isMobile ? "4px 6px" : "5px",
              gap: ".4rem",
              cursor: "pointer",
              margin: 0,
            }}
            onClick={handleDownloadReceipt}
          >
            <ReceiptIcon
              sx={{ color: "#0056D2", fontSize: isMobile ? "18px" : "24px" }}
            />
            {!isMobile && "RECEIPT"}
          </h4>
        </div>
        {quantity_each?.map((item, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              width: "100%",
              justifyContent: "space-between",
              alignItems: "center",
              padding: isMobile ? "12px 0" : "15px 0",
              gap: isMobile ? "10px" : "0",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
                cursor: "pointer",
                flex: 1,
              }}
              onClick={() => {
                window.open(
                  `https://kathys-new-alpha.vercel.app/product/${item.product_id}`,
                  "_blank"
                );
              }}
            >
              <img
                src={item?.images?.[0]}
                style={{
                  height: isMobile ? "60px" : "80px",
                  objectFit: "cover",
                  objectPosition: "top",
                  width: isMobile ? "60px" : "80px",
                  borderRadius: "8px",
                  background: "#f0f0f0",
                }}
                alt="Product"
              />
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "5px",
                  flex: 1,
                }}
              >
                <h5
                  style={{
                    fontSize: isMobile ? "0.875rem" : "1rem",
                    fontWeight: "500",
                    margin: 0,
                  }}
                >
                  {item.product_name}
                </h5>

                <h5
                  style={{
                    fontSize: isMobile ? "0.75rem" : "0.875rem",
                    color: "gray",
                    margin: 0,
                  }}
                >
                  {item.variant_details &&
                    Object.entries(item.variant_details).map(
                      ([key, value], index) => (
                        <span key={key}>
                          {index > 0 ? ", " : ""}
                          {value}
                        </span>
                      )
                    )}
                </h5>

                <h5
                  style={{
                    fontSize: isMobile ? "0.75rem" : "0.875rem",
                    color: "gray",
                    margin: 0,
                  }}
                >
                  {item.quantity} x ₹{item.discounted_price}
                </h5>
              </div>
            </div>
            <h4
              style={{
                fontSize: isMobile ? "0.875rem" : "1rem",
                fontWeight: "500",
                margin: 0,
              }}
            >
              ₹{item.quantity * item.discounted_price}
            </h4>
          </div>
        ))}
      </div>
      <div style={{ borderBottom: "1px solid #e0e0e0", width: "100%" }}></div>

      {/* Order Summary */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          padding: isMobile ? "16px 0" : "20px 0",
          gap: "12px",
        }}
      >
        <div
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <h4
            style={{
              fontSize: isMobile ? "0.875rem" : "1rem",
              color: "gray",
              fontWeight: "400",
              margin: 0,
            }}
          >
            Item Total
          </h4>
          <h4
            style={{
              fontSize: isMobile ? "0.875rem" : "1rem",
              fontWeight: "400",
              margin: 0,
            }}
          >
            ₹{items_total - (delivery || 0) + (coupon_discount || 0)}
          </h4>
        </div>
        <div
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <h4
            style={{
              fontSize: isMobile ? "0.875rem" : "1rem",
              color: "gray",
              fontWeight: "400",
              margin: 0,
            }}
          >
            Coupon Discount{coupon_code ? ` (${coupon_code})` : ""}
          </h4>
          <h4
            style={{
              fontSize: isMobile ? "0.875rem" : "1rem",
              fontWeight: "400",
              margin: 0,
            }}
          >
            ₹{coupon_discount || 0}
          </h4>
        </div>
        <div
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <h4
            style={{
              fontSize: isMobile ? "0.875rem" : "1rem",
              color: "gray",
              fontWeight: "400",
              margin: 0,
            }}
          >
            Delivery
          </h4>
          <h4
            style={{
              fontSize: isMobile ? "0.875rem" : "1rem",
              fontWeight: "400",
              margin: 0,
            }}
          >
            ₹{delivery || 0}
          </h4>
        </div>
        <div
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            marginTop: "10px",
          }}
        >
          <h4
            style={{
              fontSize: isMobile ? "1rem" : "1.1rem",
              fontWeight: "500",
              margin: 0,
            }}
          >
            Grand Total
          </h4>
          <h4
            style={{
              fontSize: isMobile ? "1rem" : "1.1rem",
              fontWeight: "500",
              margin: 0,
            }}
          >
            ₹{items_total}
          </h4>
        </div>
      </div>
    </div>
  );

  const CustomerDetailsContent = () => (
    <div style={{ padding: isMobile ? "16px" : "25px" }}>
      {/* Customer Details */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <h4
          style={{
            fontSize: isMobile ? "0.9rem" : "1rem",
            color: "gray",
            fontWeight: "500",
            marginBottom: isMobile ? "16px" : "20px",
          }}
        >
          CUSTOMER DETAILS
        </h4>
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            width: "100%",
            gap: isMobile ? "16px" : "20px",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              width: isMobile ? "100%" : "50%",
            }}
          >
            <h4
              style={{
                fontSize: isMobile ? "0.875rem" : "1rem",
                fontWeight: "500",
                margin: 0,
              }}
            >
              Name
            </h4>
            <h4
              style={{
                fontSize: isMobile ? "0.8rem" : "0.9rem",
                color: "gray",
                margin: 0,
              }}
            >
              {customer_details?.name || "N/A"}
            </h4>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              width: isMobile ? "100%" : "50%",
            }}
          >
            <h4
              style={{
                fontSize: isMobile ? "0.875rem" : "1rem",
                fontWeight: "500",
                margin: 0,
              }}
            >
              Mobile Number
            </h4>
            <h4
              style={{
                fontSize: isMobile ? "0.8rem" : "0.9rem",
                color: "gray",
                margin: 0,
              }}
            >
              {customer_details?.mobile_number || "N/A"}
            </h4>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            width: "100%",
            gap: isMobile ? "16px" : "20px",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              width: isMobile ? "100%" : "50%",
            }}
          >
            <h4
              style={{
                fontSize: isMobile ? "0.875rem" : "1rem",
                fontWeight: "500",
                margin: 0,
              }}
            >
              Address
            </h4>
            <h4
              style={{
                fontSize: isMobile ? "0.8rem" : "0.9rem",
                color: "gray",
                margin: 0,
              }}
            >
              {customer_details?.address || "N/A"}
            </h4>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              width: isMobile ? "100%" : "50%",
            }}
          >
            <h4
              style={{
                fontSize: isMobile ? "0.875rem" : "1rem",
                fontWeight: "500",
                margin: 0,
              }}
            >
              Locality/Area
            </h4>
            <h4
              style={{
                fontSize: isMobile ? "0.8rem" : "0.9rem",
                color: "gray",
                margin: 0,
              }}
            >
              {customer_details?.locality_area || "N/A"}
            </h4>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            width: "100%",
            gap: isMobile ? "16px" : "20px",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              width: isMobile ? "100%" : "50%",
            }}
          >
            <h4
              style={{
                fontSize: isMobile ? "0.875rem" : "1rem",
                fontWeight: "500",
                margin: 0,
              }}
            >
              City
            </h4>
            <h4
              style={{
                fontSize: isMobile ? "0.8rem" : "0.9rem",
                color: "gray",
                margin: 0,
              }}
            >
              {customer_details?.city || "N/A"}
            </h4>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              width: isMobile ? "100%" : "50%",
            }}
          >
            <h4
              style={{
                fontSize: isMobile ? "0.875rem" : "1rem",
                fontWeight: "500",
                margin: 0,
              }}
            >
              Pin Code
            </h4>
            <h4
              style={{
                fontSize: isMobile ? "0.8rem" : "0.9rem",
                color: "gray",
                margin: 0,
              }}
            >
              {customer_details?.pincode || "N/A"}
            </h4>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            width: "100%",
            gap: isMobile ? "16px" : "20px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              width: isMobile ? "100%" : "50%",
            }}
          >
            <h4
              style={{
                fontSize: isMobile ? "0.875rem" : "1rem",
                fontWeight: "500",
                margin: 0,
              }}
            >
              State
            </h4>
            <h4
              style={{
                fontSize: isMobile ? "0.8rem" : "0.9rem",
                color: "gray",
                margin: 0,
              }}
            >
              {customer_details?.state || "N/A"}
            </h4>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              width: isMobile ? "100%" : "50%",
            }}
          >
            <h4
              style={{
                fontSize: isMobile ? "0.875rem" : "1rem",
                fontWeight: "500",
                margin: 0,
              }}
            >
              Payment
            </h4>
            <h4
              style={{
                fontSize: isMobile ? "0.8rem" : "0.9rem",
                color: "gray",
                margin: 0,
              }}
            >
              {payment_mode || "N/A"}
            </h4>
          </div>
        </div>
      </div>
      <div
        style={{
          borderBottom: "1px solid #e0e0e0",
          width: "100%",
          margin: "20px 0",
        }}
      ></div>

      {/* Additional Details */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <h4
          style={{
            fontSize: isMobile ? "0.9rem" : "1rem",
            color: "gray",
            fontWeight: "500",
            marginBottom: isMobile ? "16px" : "20px",
          }}
        >
          ADDITIONAL DETAILS
        </h4>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "2px",
            paddingBottom: "2rem",
            width: "100%",
          }}
        >
          <h4
            style={{
              fontSize: isMobile ? "0.875rem" : "1rem",
              fontWeight: "500",
              margin: 0,
            }}
          >
            Email Address
          </h4>
          <h4
            style={{
              fontSize: isMobile ? "0.8rem" : "0.9rem",
              color: "gray",
              margin: 0,
            }}
          >
            {customer_details?.email || "N/A"}
          </h4>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            width: "100%",
          }}
        >
          <h4
            style={{
              fontSize: isMobile ? "0.875rem" : "1rem",
              fontWeight: "500",
              margin: 0,
            }}
          >
            Notes
          </h4>
          <h4
            style={{
              fontSize: isMobile ? "0.8rem" : "0.9rem",
              color: "gray",
              margin: 0,
            }}
          >
            {additional_info || "N/A"}
          </h4>
        </div>
      </div>
      <div
        style={{
          borderBottom: "1px solid #e0e0e0",
          width: "100%",
          margin: "20px 0",
        }}
      ></div>

      {/* Delivery Details */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <h4
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: isMobile ? "0.9rem" : "1rem",
            color: "gray",
            fontWeight: "500",
            marginBottom: isMobile ? "16px" : "20px",
          }}
        >
          DELIVERY DETAILS
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setShippingDetails({
                shippingPartner: trackingInfo?.courier || "",
                trackingNumber: trackingInfo?.trackingId || "",
              });
              setShip(true);
            }}
            style={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}
          >
            Edit
          </Button>
        </h4>

        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            width: "100%",
            gap: isMobile ? "16px" : "20px",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              width: isMobile ? "100%" : "50%",
            }}
          >
            <h4
              style={{
                fontSize: isMobile ? "0.875rem" : "1rem",
                fontWeight: "500",
                margin: 0,
              }}
            >
              Courier Name
            </h4>
            <h4
              style={{
                fontSize: isMobile ? "0.8rem" : "0.9rem",
                color: "gray",
                margin: 0,
              }}
            >
              {trackingInfo?.courier || "N/A"}
            </h4>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              width: isMobile ? "100%" : "50%",
            }}
          >
            <h4
              style={{
                fontSize: isMobile ? "0.875rem" : "1rem",
                fontWeight: "500",
                margin: 0,
              }}
            >
              Tracking ID
            </h4>
            <h4
              style={{
                fontSize: isMobile ? "0.8rem" : "0.9rem",
                color: "gray",
                margin: 0,
              }}
            >
              {trackingInfo?.trackingId || "N/A"}
            </h4>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            width: isMobile ? "100%" : "50%",
          }}
        >
          <h4
            style={{
              fontSize: isMobile ? "0.875rem" : "1rem",
              fontWeight: "500",
              margin: 0,
            }}
          >
            Order Invoice
          </h4>
          <h4
            style={{
              fontSize: isMobile ? "0.8rem" : "0.9rem",
              color: "blue",
              fontWeight: "400",
              cursor: "pointer",
              textDecoration: "underline",
              margin: 0,
            }}
            onClick={handleDownloadInvoice}
          >
            Download
          </h4>
        </div>
      </div>
    </div>
  );

  const OrderStatusContent = () => (
    <div style={{ padding: isMobile ? "16px" : "25px" }}>
      <Box sx={{ maxWidth: 400, margin: "auto" }}>
        <Stepper
          activeStep={
            status?.toLowerCase() === "pending"
              ? 1
              : status?.toLowerCase() === "accepted"
              ? 2
              : status?.toLowerCase() === "shipped"
              ? 3
              : status?.toLowerCase() === "rejected" ||
                status?.toLowerCase() === "expired"
              ? 1
              : 0
          }
          orientation="vertical"
        >
          {getSteps(status, trackingInfo).map((step, index) => (
            <Step
              key={step.label}
              completed={step.completed}
              active={step.active}
            >
              <StepLabel
                optional={
                  <Typography variant="caption" color="text.secondary">
                    {step.description}
                  </Typography>
                }
                StepIconComponent={() => (
                  <Box
                    sx={{
                      backgroundColor: step.completed
                        ? status?.toLowerCase() === "rejected"
                          ? "#f44336"
                          : status?.toLowerCase() === "expired"
                          ? "#757575"
                          : "#4caf50"
                        : step.active
                        ? "#1976d2"
                        : "#e0e0e0",
                      color: "white",
                      borderRadius: "50%",
                      width: 35,
                      height: 35,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {step.completed ? (
                      status?.toLowerCase() === "rejected" ? (
                        <Close fontSize="small" />
                      ) : (
                        <CheckIcon fontSize="small" />
                      )
                    ) : (
                      step.icon
                    )}
                  </Box>
                )}
              >
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
    </div>
  );

  // Mobile View with Tabs
  if (isMobile) {
    return (
      <div style={{ width: "100%", height: "100%" }}>
        <Paper square>
          <Tabs
            value={mobileTabValue}
            onChange={(e, newValue) => setMobileTabValue(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab label="Order Details" />
            <Tab label="Customer Info" />
            <Tab label="Status" />
          </Tabs>
        </Paper>

        <div
          style={{
            background: "white",
            height: "calc(100vh - 200px)",
            overflowY: "auto",
          }}
        >
          {mobileTabValue === 0 && <OrderDetailsContent />}
          {mobileTabValue === 1 && <CustomerDetailsContent />}
          {mobileTabValue === 2 && <OrderStatusContent />}
        </div>

        <ShippingDetailsModel
          open={ship}
          handleChange={handleChange}
          shippingDetails={shippingDetails}
          handleSubmit={async () => {
            const success = await updateOrderToShipped(
              id,
              shippingDetails.shippingPartner,
              shippingDetails.trackingNumber
            );
            if (success) {
              setShippingDetails({ shippingPartner: "", trackingNumber: "" });
            }
          }}
          onClose={() => {
            setShip(false);
            setShippingDetails({ shippingPartner: "", trackingNumber: "" });
          }}
        />
        <CustomDialog
          open={open}
          title="Accept order"
          description="Accept this order?"
          onPrimaryClick={() => {
            handleAcceptOrder();
          }}
          onSecondaryClick={() => {
            setOpen(false);
          }}
        />
        <CustomDialog
          open={open2}
          title="Reject order"
          description="Reject this order?"
          onPrimaryClick={() => {
            handleRejectOrder();
          }}
          onSecondaryClick={() => {
            setOpen2(false);
          }}
        />
      </div>
    );
  }

  // Desktop/Tablet View
  return (
    <div
      style={{
        display: "flex",
        gap: "20px",
        width: "100%",
        maxWidth: "1400px",
        alignItems: "start",
        justifyContent: "center",
        margin: "auto",
        flexDirection: isTablet ? "column" : "row",
        padding: isTablet ? "0" : "0 20px",
      }}
    >
      <div
        className="scroll-content"
        style={{
          background: "white",
          overflowY: "auto",
          width: isTablet ? "100%" : "70%",
          height: isTablet ? "auto" : "80vh",
          maxHeight: isTablet ? "none" : "80vh",
          borderRadius: "10px",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
          padding: isTablet ? "20px" : "25px",
        }}
      >
        <OrderDetailsContent />
        <div
          style={{
            borderBottom: "1px solid #e0e0e0",
            width: "100%",
            margin: "20px 0",
          }}
        ></div>
        <CustomerDetailsContent />
      </div>

      <div
        style={{
          width: isTablet ? "100%" : "30%",
          height: "max-content",
          background: "white",
          borderRadius: "10px",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
          padding: isTablet ? "20px" : "25px",
        }}
      >
        <OrderStatusContent />
      </div>

      <ShippingDetailsModel
        open={ship}
        handleChange={handleChange}
        shippingDetails={shippingDetails}
        handleSubmit={async () => {
          const success = await updateOrderToShipped(
            id,
            shippingDetails.shippingPartner,
            shippingDetails.trackingNumber
          );
          if (success) {
            setShippingDetails({ shippingPartner: "", trackingNumber: "" });
          }
        }}
        onClose={() => {
          setShip(false);
          setShippingDetails({ shippingPartner: "", trackingNumber: "" });
        }}
      />
      <CustomDialog
        open={open}
        title="Accept order"
        description="Accept this order?"
        onPrimaryClick={() => {
          handleAcceptOrder();
        }}
        onSecondaryClick={() => {
          setOpen(false);
        }}
      />
      <CustomDialog
        open={open2}
        title="Reject order"
        description="Reject this order?"
        onPrimaryClick={() => {
          handleRejectOrder();
        }}
        onSecondaryClick={() => {
          setOpen2(false);
        }}
      />
    </div>
  );
};

export default ViewOrder; // OrdersTab.js
